import { Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import { Location } from '@angular/common';
import { CommonService } from '../../helpers/common.service';
import { catchError, forkJoin, map, of } from 'rxjs';

interface TabData {
  Date: string;
  Name: string;
  Action: string;
}

interface MtfDashboard {
  totalCalls: number;
  positiveCalls: number;
  openCalls: number;
  closedCalls: number;
}

interface MtfStockItem extends TabData {
  target: string;
  status: string;
}

interface TabItem {
  id: string;
  label: string;
  dataUrl: string;
}

@Component({
  standalone:false,
  selector: 'app-market-mantra',
  templateUrl: './market-mantra.component.html',
  styleUrls: ['./market-mantra.component.scss'],

})
export class MarketMantraComponent implements OnInit, OnDestroy {
  pdfUrl: string;
  readonly pdfBaseUrl: string;
  showPdfPopup = false;
  isApkWebView = false;
  pdfLoading = false;

  tabItems: TabItem[] = [
    { id: 'idea2act', label: 'IDEA2ACT', dataUrl: 'idea2act' },
    { id: 'thefront', label: 'The Front Page', dataUrl: 'thefront' },
    { id: 'stocklist', label: 'Stock List', dataUrl: 'stocklist' },
    { id: 'mtf', label: 'MTF', dataUrl: 'mtf' },
    { id: 'special', label: 'Special Research Report', dataUrl: 'special' }
  ];

  selectedTab = 'idea2act';
  tabDataMap: { [key: string]: TabData[] } = {};
  tabResponseMap: { [key: string]: any } = {};
  currentTabData: TabData[] = [];
  mtfDashboard: MtfDashboard | null = null;
  mtfStockList: MtfStockItem[] = [];
  loadingTab = false;
  private originalWindowPrint: (() => void) | null = null;

  constructor(private commonService: CommonService,private location: Location) {
    this.pdfBaseUrl = this.commonService.getMarketMantraPdfUrl();
    this.pdfUrl = this.buildCacheBustedUrl(this.pdfBaseUrl);
  }

  ngOnInit(): void {
    this.isApkWebView = false;
    this.loadVisibleTabs();
  }

  ngOnDestroy(): void {
    this.togglePrintBlock(false);
  }

  private loadVisibleTabs(): void {
    this.loadingTab = true;

    const requests = this.tabItems.map((tab) =>
      this.commonService.getMarketMantraTabData(tab.dataUrl).pipe(
        map((data) => data),
        catchError((err) => {
          console.error(`Error loading tab data for ${tab.id}:`, err);
          return of(null);
        })
      )
    );

    forkJoin(requests).subscribe((results) => {
      const availableTabs: TabItem[] = [];

      results.forEach((tabResponse, index) => {
        const tab = this.tabItems[index];

        this.tabResponseMap[tab.id] = tabResponse;

        if (tab.id === 'mtf') {
          const mtfData = this.normalizeMtfData(tabResponse);
          this.mtfDashboard = mtfData.dashboard;
          this.mtfStockList = this.sortTabDataDesc(mtfData.stockList) as MtfStockItem[];
          if (this.mtfStockList.length > 0 || !!this.mtfDashboard) {
            availableTabs.push(tab);
          }
        } else {
          const tabData = this.sortTabDataDesc(this.normalizeTabData(tabResponse));
          this.tabDataMap[tab.id] = tabData;
          if (tabData.length > 0) {
            availableTabs.push(tab);
          }
        }
      });

      this.tabItems = availableTabs;

      if (this.tabItems.length > 0) {
        this.selectedTab = this.tabItems[0].id;
        this.applySelectedTabData(this.selectedTab);
      } else {
        this.selectedTab = '';
        this.currentTabData = [];
        this.mtfDashboard = null;
        this.mtfStockList = [];
      }

      this.loadingTab = false;
    });
  }

  fetchTabData(tabId: string): void {
    if (this.tabResponseMap[tabId]) {
      this.applySelectedTabData(tabId);
      return;
    }

    this.loadingTab = true;
    const tab = this.tabItems.find(t => t.id === tabId);
    if (!tab) {
      this.loadingTab = false;
      return;
    }

    this.commonService.getMarketMantraTabData(tab.dataUrl).subscribe({
      next: (data) => {
        this.tabResponseMap[tabId] = data;
        this.applySelectedTabData(tabId);
        this.loadingTab = false;
      },
      error: (err) => {
        console.error(`Error loading tab data for ${tabId}:`, err);
        this.currentTabData = [];
        this.loadingTab = false;
      }
    });
  }

  private normalizeTabData(data: any): TabData[] {
    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.stockList)) {
      return data.stockList;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.items)) {
      return data.items;
    }

    if (Array.isArray(data?.result)) {
      return data.result;
    }

    return [];
  }

  private normalizeMtfData(data: any): { dashboard: MtfDashboard | null; stockList: MtfStockItem[] } {
    const source = data?.dashboard || data?.stockList
      ? data
      : (data?.data?.dashboard || data?.data?.stockList)
        ? data.data
        : (data?.result?.dashboard || data?.result?.stockList)
          ? data.result
          : (data?.items?.dashboard || data?.items?.stockList)
            ? data.items
            : {};

    const stockList = Array.isArray(source?.stockList) ? source.stockList : [];
    const dashboard = source?.dashboard || null;

    return {
      dashboard,
      stockList
    };
  }

  private applySelectedTabData(tabId: string): void {
    if (tabId === 'mtf') {
      const mtfData = this.normalizeMtfData(this.tabResponseMap[tabId]);
      this.mtfDashboard = mtfData.dashboard;
      this.mtfStockList = this.sortTabDataDesc(mtfData.stockList) as MtfStockItem[];
      this.currentTabData = [];
      return;
    }

    const normalizedData = this.normalizeTabData(this.tabResponseMap[tabId]);
    const sortedData = this.sortTabDataDesc(normalizedData);
    this.tabDataMap[tabId] = sortedData;
    this.currentTabData = sortedData;
  }

  selectTab(tabId: string): void {
    this.selectedTab = tabId;
    this.fetchTabData(tabId);
  }

  openActionPdf(pdfUrl: string): void {
    this.pdfUrl = this.commonService.getMarketMantraActionPdfUrl(pdfUrl);
    this.pdfLoading = true;
    this.showPdfPopup = true;
    this.togglePrintBlock(true);
  }

  onPdfLoaded(): void {
    this.pdfLoading = false;
  }

  onPdfLoadError(): void {
    this.pdfLoading = false;
  }

  openPdf(): void {
    this.pdfUrl = this.buildCacheBustedUrl(this.pdfBaseUrl);
    this.pdfLoading = true;
    this.showPdfPopup = true;
    this.togglePrintBlock(true);
  }

  closePdf(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.showPdfPopup = false;
    this.togglePrintBlock(false);
  }

  blockAction(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    this.blockPdfKeyboardShortcuts(event);
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(event: KeyboardEvent): void {
    this.blockPdfKeyboardShortcuts(event);
  }

  @HostListener('window:keypress', ['$event'])
  onWindowKeypress(event: KeyboardEvent): void {
    this.blockPdfKeyboardShortcuts(event);
  }

  @HostListener('window:keyup', ['$event'])
  onWindowKeyup(event: KeyboardEvent): void {
    this.blockPdfKeyboardShortcuts(event);
  }

  private blockPdfKeyboardShortcuts(event: KeyboardEvent): void {
    if (!this.showPdfPopup) {
      return;
    }

    const key = (event.key || '').toLowerCase();
    const code = event.code || '';
    const isModifierPressed = event.ctrlKey || event.metaKey;
    const isBlockedKey = ['p', 's', 'c', 'x', 'a'].includes(key) || ['KeyA', 'KeyC', 'KeyX', 'KeyS', 'KeyP'].includes(code);

    if (isModifierPressed && isBlockedKey) {
      event.preventDefault();
      event.stopPropagation();
      (event as any).returnValue = false;
      
    }
    // Extra: block F12 (dev tools) & print key
  if (key === 'f12' || key === 'printscreen') {
    event.preventDefault();
    event.stopPropagation();
  }
  }

  formatDateForDisplay(dateText: string): string {
    if (!dateText) {
      return '';
    }

    const parts = dateText.split(/[-/]/);
    if (parts.length !== 3) {
      return dateText;
    }

    const day = Number(parts[0]);
    const month = Number(parts[1]) - 1;
    const year = Number(parts[2]);
    const date = new Date(year, month, day);

    if (Number.isNaN(date.getTime())) {
      return dateText;
    }

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  private sortTabDataDesc(data: TabData[]): TabData[] {
    return [...data].sort((a, b) => this.parseDateValue(b.Date) - this.parseDateValue(a.Date));
  }

  private parseDateValue(dateText: string): number {
    if (!dateText) {
      return 0;
    }

    const parts = dateText.split(/[-/]/);
    if (parts.length === 3) {
      const day = Number(parts[0]);
      const month = Number(parts[1]) - 1;
      const year = Number(parts[2]);
      const parsed = new Date(year, month, day).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    const parsed = new Date(dateText).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private buildCacheBustedUrl(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    // Prevent stale PDF responses from browser/proxy cache with formatted timestamp.
    const currentDateTime = this.getFormattedDateTime();
    return `${url}${separator}t=${currentDateTime}`;
  }

  private getFormattedDateTime(): string {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hour = String(now.getHours()).padStart(2, '0');
    const minute = String(now.getMinutes()).padStart(2, '0');
    return `${day}${month}${year}${hour}${minute}`;
  }

  private togglePrintBlock(shouldBlock: boolean): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (shouldBlock) {
      if (!this.originalWindowPrint) {
        this.originalWindowPrint = window.print.bind(window);
      }
      window.print = () => undefined;
      return;
    }

    if (this.originalWindowPrint) {
      window.print = this.originalWindowPrint;
      this.originalWindowPrint = null;
    }
  }

  	goBack(){
		
    	this.location.back();
	}
}
